import { useState } from 'react';
import { esmsApi } from '../../services/esms-api';
import type { EsmsMessage } from '../../types/esms';
import InfoTooltip from '../revenue/InfoTooltip';
import EsmsErrorBox from './EsmsErrorBox';

function fmt(v: number): string { return v.toLocaleString('en-US'); }

function resultLabel(sendResult: number): string {
  if (sendResult === 1) return 'Thành công';
  if (sendResult === 0) return 'Thất bại';
  return `Code ${sendResult}`;
}

interface Props {
  isDark: boolean;
  from: string;
  to: string;
  disabled: boolean;
}

export default function EsmsPhoneLookup({ isDark, from, to, disabled }: Props) {
  const [phone, setPhone] = useState('');
  const [messages, setMessages] = useState<EsmsMessage[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queriedPhone, setQueriedPhone] = useState<string | null>(null);

  const search = async () => {
    const p = phone.trim();
    if (!p) return;
    setLoading(true); setError(null); setMessages(null);
    try {
      const res = await esmsApi.getMessagesByPhone(p, from, to);
      setMessages(res.messages);
      setQueriedPhone(res.phone);
    } catch (e: any) {
      const status = e?.response?.status;
      const serverMsg = e?.response?.data?.error || e?.response?.data?.message;
      const serverStack = e?.response?.data?.stack;
      const parts: string[] = [];
      if (status) parts.push(`HTTP ${status} GET /esms/messages-by-phone`);
      if (serverMsg) parts.push(`Server: ${serverMsg}`);
      if (serverStack) parts.push(serverStack);
      if (!parts.length) parts.push(e?.message || 'Lỗi tra cứu');
      setError(parts.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') search();
  };

  return (
    <div className={`backdrop-blur rounded-lg p-4 sm:p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Tra cứu SMS theo SĐT
        <InfoTooltip isDark={isDark} text={'Nhập số điện thoại để xem toàn bộ SMS đã gửi cho số đó trong kỳ (bao gồm nội dung, loại, brandname, chi phí, trạng thái).\nMatch theo 9 chữ số cuối — chấp nhận +84 hoặc 0 đầu.\nChỉ hỗ trợ khoảng thời gian ≤ 31 ngày.\n\nNguồn: eSMS GetSmsSentData_V2.'} />
      </h3>

      {disabled ? (
        <div className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
          Tính năng này chỉ khả dụng khi khoảng thời gian ≤ 31 ngày. Hãy thu hẹp phạm vi để dùng.
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Nhập số điện thoại (vd: 0901234567)"
              className={`flex-1 px-3 py-2 rounded border text-sm font-mono ${isDark ? 'bg-slate-800 border-white/10 text-slate-100 placeholder-slate-500' : 'bg-white border-gray-300 text-gray-900'}`}
            />
            <button
              onClick={search}
              disabled={loading || !phone.trim()}
              className={`px-4 py-2 rounded text-sm font-medium ${loading || !phone.trim() ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}
            >
              {loading ? 'Đang tra...' : 'Tra cứu'}
            </button>
          </div>

          {error && (
            <div className="mb-2"><EsmsErrorBox error={error} isDark={isDark} compact /></div>
          )}

          {messages !== null && !loading && !error && (
            <div className={`text-sm mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
              Tìm thấy <span className="font-semibold">{fmt(messages.length)}</span> SMS cho số <span className="font-mono">{queriedPhone}</span> trong {from} — {to}.
            </div>
          )}

          {messages && messages.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}>
                    <th className={`text-left px-2 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Thời gian</th>
                    <th className={`text-left px-2 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Loại</th>
                    <th className={`text-left px-2 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Brandname</th>
                    <th className={`text-left px-2 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Nội dung</th>
                    <th className={`text-right px-2 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Giá (VND)</th>
                    <th className={`text-left px-2 py-2 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>Kết quả</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => (
                    <tr key={m.smsId} className={isDark ? 'border-b border-white/5' : 'border-b border-gray-100'} title={m.campaign ? `Campaign: ${m.campaign}` : ''}>
                      <td className={`px-2 py-2 whitespace-nowrap text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{m.sentTime ? m.sentTime.replace('T', ' ').substring(0, 19) : '—'}</td>
                      <td className={`px-2 py-2 text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{m.label}</td>
                      <td className={`px-2 py-2 text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{m.brandname || '—'}</td>
                      <td className={`px-2 py-2 text-xs max-w-md ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                        <div className="whitespace-pre-wrap break-words">{m.content}</div>
                      </td>
                      <td className={`px-2 py-2 text-right font-mono text-xs ${isDark ? 'text-amber-300' : 'text-gray-900'}`}>{fmt(m.sellPrice)}</td>
                      <td className={`px-2 py-2 text-xs ${m.sendResult === 1 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-rose-400' : 'text-rose-600')}`}>{resultLabel(m.sendResult)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
